import type { ReactNode } from 'react';
import { useController, useFormContext, type FieldValues, type Path } from 'react-hook-form';
import { FormField } from '@/shared/ui/field';
import { Checkbox } from '@/shared/ui/checkbox';
import { cn } from '@/shared/ui/cn';
import { ChoiceCardGroup, type ChoiceOption } from '@/shared/ui/choice-card';
import { Label } from '@/shared/ui/label';
import { RadioOptions, type RadioOption } from '@/shared/ui/radio-group';

import { useBound } from './fields';

/** Escolha única em rádio, ligada ao RHF (o `RadioGroup` do "Gerar CNAB"). */
export function RadioFormField<V extends FieldValues>({
  name,
  label,
  options,
  required,
}: {
  readonly name: Path<V>;
  readonly label: ReactNode;
  readonly options: readonly RadioOption[];
  readonly required?: boolean;
}) {
  const { control } = useFormContext<V>();
  const { field, fieldState } = useController({ name, control });
  return (
    <FormField
      name={name}
      label={label}
      required={required === true}
      error={fieldState.error?.message}
    >
      {(props) => (
        <RadioOptions
          aria-labelledby={props['aria-labelledby']}
          {...(props['aria-describedby'] ? { 'aria-describedby': props['aria-describedby'] } : {})}
          value={typeof field.value === 'string' ? field.value : ''}
          onValueChange={(value) => {
            field.onChange(value);
            field.onBlur();
          }}
          options={options}
        />
      )}
    </FormField>
  );
}

/**
 * Várias escolhas em caixas, ligadas ao RHF (o `SelectFormField multiple` do legado). Grava os valores na
 * ordem do clique. Somente leitura só pela prop: o campo não herda o do `FormScreen`.
 */
export function CheckboxGroupFormField<V extends FieldValues>({
  name,
  label,
  options,
  hint,
  required,
  disabled,
  readOnly,
  empty,
  columns,
  className,
}: {
  readonly name: Path<V>;
  readonly label: ReactNode;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly hint?: ReactNode;
  /** Asterisco e "(obrigatório)" no rótulo; a regra continua no schema. */
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  /** Texto no lugar das caixas quando não há opção; sem ele, o grupo fica vazio. */
  readonly empty?: ReactNode;
  /** Colunas a partir de 768 px; sem ela, a grade de sempre. */
  readonly columns?: 2 | 3;
  readonly className?: string;
}) {
  const { control } = useFormContext<V>();
  const { field, fieldState } = useController({ name, control });
  const selected: readonly string[] = Array.isArray(field.value) ? (field.value as string[]) : [];
  const locked = disabled === true || readOnly === true;
  return (
    <FormField
      name={name}
      label={label}
      hint={hint}
      error={fieldState.error?.message}
      {...(required === undefined ? {} : { required })}
      {...(disabled === undefined ? {} : { disabled })}
      {...(readOnly === undefined ? {} : { readOnly })}
      {...(className === undefined ? {} : { className })}
    >
      {(props) =>
        options.length === 0 && empty !== undefined ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          <div
            role="group"
            aria-labelledby={props['aria-labelledby']}
            {...(props['aria-describedby'] ? { 'aria-describedby': props['aria-describedby'] } : {})}
            className={cn(
              'grid gap-2',
              columns === undefined && '@min-[480px]/form:grid-cols-2',
              columns === 2 && 'md:grid-cols-2',
              columns === 3 && 'md:grid-cols-3',
            )}
          >
            {options.map((option) => {
              const id = `${props.id}-${option.value}`;
              return (
                <div key={option.value} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={selected.includes(option.value)}
                    {...(locked ? { disabled: true } : {})}
                    onCheckedChange={(checked) => {
                      field.onChange(
                        checked === true
                          ? [...selected, option.value]
                          : selected.filter((value) => value !== option.value),
                      );
                      field.onBlur();
                    }}
                  />
                  {/* Rótulo técnico longo (escopo do Cognito) quebra em vez de vazar no iPhone. */}
                  <Label htmlFor={id} className="min-w-0 font-normal [overflow-wrap:anywhere]">
                    {option.label}
                  </Label>
                </div>
              );
            })}
          </div>
        )
      }
    </FormField>
  );
}

/**
 * Cards de escolha ligados ao RHF (U53): única grava o `value` da opção; `multiple` grava a lista. No passo com
 * `autoAdvance`, a escolha explícita avança o fluxo (`ChoiceCommitProvider` do `FlowScreen`, W07).
 */
export function ChoiceCardFormField<V extends FieldValues>({
  name,
  label,
  options,
  required,
  hint,
  multiple = false,
  hideLabel,
  className,
}: {
  readonly name: Path<V>;
  readonly label: ReactNode;
  readonly options: readonly ChoiceOption[];
  readonly required?: boolean;
  readonly hint?: ReactNode;
  readonly multiple?: boolean;
  /** O título do passo já diz o que se escolhe: o rótulo fica só para o leitor de tela. */
  readonly hideLabel?: boolean;
  readonly className?: string;
}) {
  const { field, error, readOnly } = useBound<V>({ name });
  return (
    <FormField
      name={name}
      label={label}
      required={required === true}
      hint={hint}
      error={error}
      readOnly={readOnly}
      {...(hideLabel === undefined ? {} : { hideLabel })}
      {...(className === undefined ? {} : { className })}
    >
      {(props) => {
        const aria = {
          id: props.id,
          'aria-labelledby': props['aria-labelledby'],
          'aria-describedby': props['aria-describedby'],
          'aria-invalid': props['aria-invalid'],
          'aria-required': props['aria-required'],
          disabled: props.disabled || readOnly,
          options,
        };
        return multiple ? (
          <ChoiceCardGroup
            {...aria}
            multiple
            value={Array.isArray(field.value) ? (field.value as string[]) : []}
            onValueChange={(value) => {
              field.onChange(value);
              field.onBlur();
            }}
          />
        ) : (
          <ChoiceCardGroup
            {...aria}
            name={props.name}
            value={typeof field.value === 'string' ? field.value : null}
            onValueChange={(value) => {
              field.onChange(value);
              field.onBlur();
            }}
          />
        );
      }}
    </FormField>
  );
}
